import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n";
import { MaskLine } from "../lib/motion.jsx";
import { LangSwitch } from "../components/LangSwitch";

const LOGO_FC = "/inkad-logo.png";
const LOGO_JEUGD = "/inkad-jeugd-logo.jpg";
const FC_BG = "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/lb9oi9lm_image00001%20%281%29.webp";
const J_BG = "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/iofv9w2z_WhatsApp%20Image%202026-08-28%20at%2020.38.46.webp";

export default function Hub() {
  const { t } = useLang();
  const nav = useNavigate();
  const [hover, setHover] = useState(null);
  const [exiting, setExiting] = useState(null);

  const go = (path, side) => {
    setExiting(side);
    setTimeout(() => nav(path), 620);
  };

  const flexFC = hover === "fc" ? "md:flex-[1.25]" : hover === "jeugd" ? "md:flex-[0.75]" : "md:flex-1";
  const flexJ = hover === "jeugd" ? "md:flex-[1.25]" : hover === "fc" ? "md:flex-[0.75]" : "md:flex-1";

  return (
    <div className="relative flex h-[100dvh] w-full flex-col overflow-hidden bg-[#050505] text-white" data-testid="hub-page">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-5 md:px-10 py-5">
        <div className="flex items-center gap-2">
          <span className="font-cabinet text-lg font-extrabold tracking-tight">INKAD</span>
          <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-white/40 mt-0.5">Diegem</span>
        </div>
        <LangSwitch dark />
      </div>

      {/* Center title: op mobiel een eigen blok bovenaan (geen overlap), op desktop zwevend glaspaneel */}
      <div className="pointer-events-none z-20 flex flex-col items-center px-6 pt-14 pb-3 text-center md:absolute md:inset-0 md:justify-center md:pt-0 md:pb-0">
        <div className="md:rounded-2xl md:bg-black/50 md:px-12 md:py-9 md:backdrop-blur-md md:shadow-[0_8px_40px_rgba(0,0,0,0.45)]">
          <div className="text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-white/80 mb-2 md:mb-4">
            <MaskLine delay={0.1}>{t("hubKicker")}</MaskLine>
          </div>
          <h1 className="font-cabinet text-2xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter text-white [text-shadow:0_2px_16px_rgba(0,0,0,0.6)]">
            <MaskLine delay={0.2}>{t("hubTitle1")}</MaskLine>
            <MaskLine delay={0.32}>{t("hubTitle2")}</MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mx-auto mt-2 max-w-md text-xs sm:text-sm text-white/85 md:mt-5">
            {t("hubLead")}
          </motion.p>
        </div>
      </div>

      {/* Split panels */}
      <div className="flex min-h-0 w-full flex-1 flex-col md:h-full md:flex-row">
        {/* FC */}
        <motion.button
          data-testid="hub-fc-link"
          onClick={() => go("/fc-inkad", "fc")}
          onMouseEnter={() => setHover("fc")}
          onMouseLeave={() => setHover(null)}
          className={`group relative flex-1 ${flexFC} overflow-hidden transition-[flex] duration-500 ease-out`}
          style={{ transition: "flex 0.5s cubic-bezier(0.22,1,0.36,1)" }}
        >
          <img src={FC_BG} alt="FC Inkad" style={{ objectPosition: "center 25%" }} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
          <div className={`absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30 transition-opacity duration-500 ${hover === "jeugd" ? "opacity-90" : "opacity-70"}`} />
          <div className="relative z-10 flex h-full flex-col items-start justify-end p-5 md:p-12 text-left">
            <img src={LOGO_FC} alt="FC Inkad logo" className="h-9 md:h-14 w-auto mb-2 md:mb-5 drop-shadow-lg" data-testid="hub-fc-logo" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#CCFF00] mb-1 md:mb-2">{t("fcTag")}</span>
            <h2 className="font-anton text-3xl md:text-5xl uppercase leading-none">FC Inkad</h2>
            <p className="mt-1.5 md:mt-3 max-w-xs text-xs md:text-sm text-white/70">{t("fcDesc")}</p>
            <span className="mt-3 md:mt-6 inline-flex items-center gap-2 rounded-sm bg-[#CCFF00] px-5 py-2 md:py-2.5 text-sm font-bold text-black transition-transform duration-200 group-hover:translate-x-1">
              {t("enter")} <ArrowUpRight size={16} />
            </span>
          </div>
        </motion.button>

        {/* Jeugd */}
        <motion.button
          data-testid="hub-jeugd-link"
          onClick={() => go("/jeugd", "jeugd")}
          onMouseEnter={() => setHover("jeugd")}
          onMouseLeave={() => setHover(null)}
          className={`group relative flex-1 ${flexJ} overflow-hidden transition-[flex] duration-500 ease-out`}
          style={{ transition: "flex 0.5s cubic-bezier(0.22,1,0.36,1)" }}
        >
          <img src={J_BG} alt="Inkad Jeugd" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
          <div className={`absolute inset-0 bg-gradient-to-t from-[#5a3325] via-[#7a4835]/60 to-[#E07A5F]/20 transition-opacity duration-500 ${hover === "fc" ? "opacity-90" : "opacity-70"}`} />
          <div className="relative z-10 flex h-full flex-col items-end justify-end p-5 md:p-12 text-right">
            <img src={LOGO_JEUGD} alt="Inkad Jeugd logo" className="h-9 md:h-14 w-auto mb-2 md:mb-5 rounded-full bg-white/90 p-1 drop-shadow-lg" data-testid="hub-jeugd-logo" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#F2CC8F] mb-1 md:mb-2">{t("jeugdTag")}</span>
            <h2 className="font-fredoka text-3xl md:text-5xl font-bold leading-none text-white">Inkad Jeugd</h2>
            <p className="mt-1.5 md:mt-3 max-w-xs text-xs md:text-sm text-white/80">{t("jeugdDesc")}</p>
            <span className="mt-3 md:mt-6 inline-flex items-center gap-2 rounded-full bg-[#E07A5F] px-6 py-2 md:py-2.5 text-sm font-bold text-white transition-transform duration-200 group-hover:translate-x-1">
              {t("enter")} <ArrowUpRight size={16} />
            </span>
          </div>
        </motion.button>
      </div>

      {/* Exit overlay for isolation transition */}
      <AnimatePresence>
        {exiting && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 z-50"
            style={{ background: exiting === "fc" ? "#0A0A0A" : "#F7F5F0" }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
